<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import api from "@/axios";
import { getBaseUrl } from "@/helpers/baseUrl";

export default {
  components: { Layout, PageHeader },

  page: {
    title: "WhatsApp",
    meta: [{ name: "description", content: "WhatsApp WSAPI" }],
  },

  data() {
    return {
      title: "WhatsApp",
      items: [{ text: "WhatsApp", active: true }],

      sessionState: "loading", // loading | not_provisioned | unavailable | credential_error | pending | connected

      qrCode: null,
      qrTimer: null,
      pairPhone: "",
      pairCode: null,
      showPairInput: false,

      provisioning: false,
      provisionError: null,

      testPhone: "",
      testMessage: "",
      testResult: null,
      sending: false,
    };
  },

  mounted() {
    this.checkStatus();
  },

  beforeUnmount() {
    this.stopQrPolling();
  },

  watch: {
    sessionState(val) {
      if (val === "pending") {
        this.fetchQrCode();
        this.startQrPolling();
      } else {
        this.stopQrPolling();
      }
    },
  },

  methods: {
    checkStatus() {
      this.sessionState = "loading";
      this.provisionError = null;
      api
        .get(`${getBaseUrl()}api/wsapi/status`)
        .then((res) => {
          const d = res.data;
          if (d.error && d.error.includes("não configurada")) {
            this.sessionState = "not_provisioned";
          } else if (d.isConnected && d.isLoggedIn) {
            this.sessionState = "connected";
          } else {
            this.sessionState = "pending";
          }
        })
        .catch((err) => {
          const data = err?.response?.data || {};
          const msg = data.error || "";
          if (data.credential_error) {
            this.sessionState = "credential_error";
          } else if (msg.includes("não configurada")) {
            this.sessionState = "not_provisioned";
          } else {
            this.sessionState = "unavailable";
          }
        });
    },

    provisionar() {
      if (this.provisioning) return;
      this.provisioning = true;
      this.provisionError = null;
      api
        .get(`${getBaseUrl()}api/wsapi/provisionar`)
        .then(() => {
          this.qrCode = null;
          this.sessionState = "pending";
        })
        .catch((err) => {
          this.provisionError = err?.response?.data?.error || "Erro ao criar instância. Tente novamente.";
        })
        .finally(() => {
          this.provisioning = false;
        });
    },

    recriarInstancia() {
      if (this.provisioning) return;
      this.provisioning = true;
      this.provisionError = null;
      api
        .delete(`${getBaseUrl()}api/wsapi/credentials`)
        .then(() => {
          this.provisioning = false;
          this.provisionar();
        })
        .catch(() => {
          this.provisioning = false;
          this.provisionError = "Erro ao excluir credencial anterior. Tente novamente.";
        });
    },

    fetchQrCode() {
      api
        .get(`${getBaseUrl()}api/wsapi/qr`)
        .then((res) => {
          this.qrCode = res.data.qr || null;
        })
        .catch((err) => {
          if (err?.response?.data?.credential_error) {
            this.sessionState = "credential_error";
          }
          this.qrCode = null;
        });
    },

    startQrPolling() {
      this.qrTimer = setInterval(() => {
        if (this.sessionState !== "pending") {
          this.stopQrPolling();
          return;
        }
        this.fetchQrCode();
        this.checkStatusSilent();
      }, 20000);
    },

    stopQrPolling() {
      clearInterval(this.qrTimer);
      this.qrTimer = null;
    },

    checkStatusSilent() {
      api
        .get(`${getBaseUrl()}api/wsapi/status`)
        .then((res) => {
          if (res.data.isConnected && res.data.isLoggedIn) {
            this.sessionState = "connected";
          }
        })
        .catch((err) => {
          if (err?.response?.data?.credential_error) {
            this.sessionState = "credential_error";
          }
        });
    },

    fetchPairCode() {
      if (!this.pairPhone) return;
      api
        .get(`${getBaseUrl()}api/wsapi/paircode/${this.pairPhone}`)
        .then((res) => {
          this.pairCode = res.data.code || null;
        })
        .catch(() => {
          this.pairCode = null;
        });
    },

    formatJid(phone) {
      return phone.replace(/\D/g, "") + "@s.whatsapp.net";
    },

    async enviarTeste() {
      if (!this.testPhone || !this.testMessage) return;
      this.sending = true;
      this.testResult = null;
      try {
        await api.post(`${getBaseUrl()}api/wsapi/messages`, {
          to: this.formatJid(this.testPhone),
          text: this.testMessage,
        });
        this.testResult = { success: true, message: "Mensagem enviada com sucesso!" };
      } catch {
        this.testResult = { success: false, message: "Falha ao enviar. Verifique se o número é válido." };
      } finally {
        this.sending = false;
      }
    },

    logout() {
      api.post(`${getBaseUrl()}api/wsapi/logout`).then(() => {
        this.sessionState = "pending";
        this.testResult = null;
        this.testPhone = "";
        this.testMessage = "";
      });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- Estado: carregando -->
    <div v-if="sessionState === 'loading'" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
      <p class="mt-2 text-muted">Verificando conexão...</p>
    </div>

    <!-- Estado: não provisionado -->
    <div v-else-if="sessionState === 'not_provisioned'" class="text-center py-5">
      <i class="mdi mdi-whatsapp font-size-48 text-success"></i>
      <h4 class="mt-3">Nenhuma instância configurada</h4>
      <p class="text-muted">Crie uma instância WhatsApp para este cliente.</p>
      <button class="btn btn-success btn-lg mt-2" :disabled="provisioning" @click="provisionar">
        <span v-if="provisioning" class="spinner-border spinner-border-sm me-1"></span>
        <i v-else class="mdi mdi-plus me-1"></i>
        {{ provisioning ? 'Criando instância...' : 'Criar instância WhatsApp' }}
      </button>
      <div v-if="provisionError" class="alert alert-danger mt-3 d-inline-block text-start">{{ provisionError }}</div>
    </div>

    <!-- Estado: instância existe mas serviço inacessível -->
    <div v-else-if="sessionState === 'unavailable'" class="text-center py-5">
      <i class="mdi mdi-wifi-off font-size-48 text-warning"></i>
      <h4 class="mt-3">Serviço temporariamente indisponível</h4>
      <p class="text-muted">
        A instância WhatsApp está configurada, mas não foi possível estabelecer conexão com o servidor WSAPI.<br>
        Aguarde alguns instantes e tente novamente.
      </p>
      <button class="btn btn-outline-warning mt-2" @click="checkStatus">
        <i class="mdi mdi-refresh me-1"></i> Tentar novamente
      </button>
      <div v-if="provisionError" class="alert alert-danger mt-3 d-inline-block text-start">{{ provisionError }}</div>
    </div>

    <!-- Estado: erro ao carregar credenciais -->
    <div v-else-if="sessionState === 'credential_error'" class="text-center py-5">
      <i class="mdi mdi-alert-circle-outline font-size-48 text-danger"></i>
      <h4 class="mt-3">Erro ao carregar credenciais</h4>
      <p class="text-muted">
        Não foi possível validar as credenciais desta instância WhatsApp.<br>
        Deseja recriá-la?
      </p>
      <button class="btn btn-danger btn-lg mt-2" :disabled="provisioning" @click="recriarInstancia">
        <span v-if="provisioning" class="spinner-border spinner-border-sm me-1"></span>
        <i v-else class="mdi mdi-refresh me-1"></i>
        {{ provisioning ? 'Recriando instância...' : 'Recriar instância' }}
      </button>
      <div v-if="provisionError" class="alert alert-danger mt-3 d-inline-block text-start">{{ provisionError }}</div>
    </div>

    <!-- Estado: pareamento -->
    <div v-else-if="sessionState === 'pending'" class="text-center py-4">
      <h4>Conectar WhatsApp</h4>
      <p class="text-muted">Escaneie o QR Code com seu WhatsApp ou use o código de pareamento.</p>

      <div v-if="!showPairInput">
        <div v-if="qrCode" class="d-inline-block border p-3 rounded bg-white shadow-sm">
          <img :src="'data:image/png;base64,' + qrCode" alt="QR Code" style="width:220px;height:220px;" />
        </div>
        <div v-else class="py-4 text-muted">
          <div class="spinner-border spinner-border-sm me-2"></div> Carregando QR Code...
        </div>
        <p class="text-muted mt-2" style="font-size:12px;">Atualiza automaticamente a cada 20 segundos</p>
      </div>

      <div v-if="showPairInput" class="mt-3" style="max-width:320px;margin:0 auto;">
        <div class="input-group">
          <input
            v-model="pairPhone"
            type="text"
            class="form-control"
            placeholder="Ex: 5527999990000"
          />
          <button class="btn btn-success" @click="fetchPairCode">Gerar código</button>
        </div>
        <div v-if="pairCode" class="mt-3">
          <h3 class="text-success letter-spacing-lg">{{ pairCode }}</h3>
          <p class="text-muted">Digite este código no WhatsApp → Dispositivos conectados → Vincular dispositivo</p>
        </div>
      </div>

      <div class="mt-3">
        <button class="btn btn-outline-secondary btn-sm" @click="showPairInput = !showPairInput">
          {{ showPairInput ? 'Usar QR Code' : 'Usar código de pareamento' }}
        </button>
      </div>
    </div>

    <!-- Estado: conectado -->
    <div v-else-if="sessionState === 'connected'">
      <div class="row justify-content-center">
        <div class="col-lg-7 col-xl-6">

          <!-- Card de status -->
          <div class="card mb-4">
            <div class="card-body d-flex align-items-center gap-3">
              <span class="badge bg-success fs-6">● Conectado</span>
              <button class="btn btn-outline-danger btn-sm ms-auto" @click="logout">
                <i class="mdi mdi-logout me-1"></i> Desconectar
              </button>
            </div>
          </div>

          <!-- Card de teste de envio -->
          <div class="card">
            <div class="card-header">
              <i class="mdi mdi-send me-2 text-success"></i> Validar envio de mensagem
            </div>
            <div class="card-body">
              <div class="mb-3">
                <label class="form-label">Número de WhatsApp <span class="text-muted">(somente dígitos, com DDI)</span></label>
                <input
                  v-model="testPhone"
                  type="text"
                  class="form-control"
                  placeholder="Ex: 5511999990000"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">Mensagem</label>
                <textarea
                  v-model="testMessage"
                  class="form-control"
                  rows="3"
                  placeholder="Digite a mensagem de teste..."
                ></textarea>
              </div>
              <button
                class="btn btn-success"
                :disabled="sending || !testPhone || !testMessage"
                @click="enviarTeste"
              >
                <span v-if="sending" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="mdi mdi-send me-1"></i>
                Enviar mensagem de teste
              </button>
              <div v-if="testResult" class="mt-3 alert" :class="testResult.success ? 'alert-success' : 'alert-danger'">
                {{ testResult.message }}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

  </Layout>
</template>
